import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import 'tailwindcss/tailwind.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faStop } from '@fortawesome/free-solid-svg-icons';

// React component starts here
const ReactionGame = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const [userId] = useState(authState.user._id);
  const [reactions, setReactions] = useState([]);
  const [currentReactionIndex, setCurrentReactionIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [timer, setTimer] = useState(30);
  const [error, setError] = useState(null);
  // const [currentReaction, setCurrentReaction] = useState(null);


  useEffect(() => {
    const config = {
      url: `/reactiongame`,
      method: 'post',
      headers: { Authorization: authState.token },
      data: { userId },
    };

    fetchData(config, { showSuccessToast: false })
      .then((data) => {
        setReactions(data.reactions);
        setCurrentReactionIndex(0); // Start from the first question
      })
      .catch((error) => {
        console.error('Error fetching Reaction Game Data:', error);
        setError('Error fetching Reaction Game Data');
      });
  }, []);

  useEffect(() => {
    if (timer <= 0) {
      setFeedback('Time is up! 😔');
    } else if (currentReactionIndex < reactions.length) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer, currentReactionIndex]);

  const [submitHistory, setSubmitHistory] = useState([]); 
  const [showHistory, setShowHistory] = useState(false); 
  const currentReaction = reactions[currentReactionIndex];

  const handleSubmit = (e) => {
  e.preventDefault();

  // Check if currentReactionIndex is valid
  if (currentReactionIndex < 0 || currentReactionIndex >= reactions.length) {
    setFeedback('Error: Invalid reaction data.');
    return;
  }

  // Ensure currentReaction is valid
  const correctAnswer = currentReaction.products.join(' + ');

  const startTime = Date.now(); // Capture the time when the user starts the submission

  if (userInput.trim() === correctAnswer) {
    setFeedback('Correct! 🎉 Moving to next...');
    setTimeout(() => {
      const nextIndex = currentReactionIndex + 1;
      if (nextIndex < reactions.length) {
        setCurrentReactionIndex(nextIndex);
        setTimer(30);
        setFeedback('');
      } else {
        setCurrentReactionIndex(reactions.length); 
      }
    }, 1500);
  } else {
    setFeedback('Incorrect! Try again. 💡');

    const timeTaken = Date.now() - startTime;
    setSubmitHistory((prevHistory) => [
      ...prevHistory,
      {
        question: currentReaction.reactants.join(' + '),
        answer: userInput.trim(),
        status: userInput.trim() === correctAnswer ? 'right' : 'wrong',
        timeTaken, 
      },
    ]);

    setUserInput('');
  }
};

  const toggleHistory = () => {
    setShowHistory(!showHistory);
  };

  const handleExit = () => {
    navigate(-1);
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-red-500">{error}</p>;

  if (currentReactionIndex >= reactions.length)
    return (
      <div className="text-center mt-10">
        <h1 className="text-2xl font-bold">Congratulations! 🎉</h1>
        <p>You’ve completed all levels. Want to play again?</p>
        <button
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded shadow hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Restart Game
        </button>
      </div>
    );

  return (
    <div className='bg-black text-white [&_p]:text-white'>
      <div className="overflow-x-auto w-full reaction-game container mx-auto p-4">
        <h1 className="text-3xl font-bold text-center text-yellow-200 mt-12">Chemical Reaction Game</h1>
        <h2 className="text-lg font-bold text-center text-white mt-2">
          Dr. Gajanan Rashinkar Special for Class IX & X
        </h2>
        <h3 className="text-m font-bold text-center text-yellow-200">
          Winner of Reaction Game will be able to interact online with Dr. Gajanan Rashinkar 
        </h3>
        <div className="flex flex-col items-center gap-6">
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-green-500 h-4 rounded-full transition-all"
              style={{ width: `${(timer / 30) * 100}%` }}
            />
          </div>
          <p className="text-lg text-orange-400 mt-2">Time Left: {timer} seconds</p>
          <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
            <h2 className="text-xl font-semibold text-blue-700">Level {currentReaction.level}</h2>
            <p className="text-m font-semibold text-blue-700">{currentReaction.reactionType}</p>
            <p className="mt-2">Reactants: {currentReaction.reactants.join(' + ')}</p>
            <p className="mt-2">Products: {currentReaction.products.join(' + ')}</p>
            {currentReaction.hints && (
              <p className="mt-2 text-gray-600 italic">Hint: {currentReaction.hints}</p>
            )}
            <form onSubmit={handleSubmit} className="mt-4">
              <input
                type="text"
                className="border rounded w-full p-2"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Enter products (e.g., H2O)"
              />
              <button
                type="submit"
                className="w-full mt-4 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
              >
                Submit
              </button>
            </form>
            <p className="mt-4 text-center text-lg">{feedback}</p>
            <button
              className="mt-4 text-m text-white hover:text-green-400"
              onClick={toggleHistory}
            >
              <FontAwesomeIcon icon={faHistory} className="text-white text-m ml-3" /> View Answer History
            </button>
            <button
              className="mt-4 text-m text-white hover:bg-orange-400"
              onClick={handleExit}
            >
              <FontAwesomeIcon icon={faStop} className="text-white text-m ml-3" /> Exit
            </button>
            {showHistory && (
              <div className="mt-6 w-full max-w-md p-4 bg-gray-100 rounded shadow-md">
                <h3 className="text-m font-semibold mb-4 text-teal-400">Submission History</h3>
                <table className="table-auto w-full">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left">Question</th>
                      <th className="px-4 py-2 text-left">Answer</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Time Taken (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submitHistory.map((entry, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-blue-500">{entry.question}</td>
                        <td className="px-4 py-2 text-blue-500">{entry.answer}</td>
                        <td className="px-4 py-2 text-blue-500">
                          {entry.status === 'right' ? '✅ Correct' : '❌ Incorrect'}
                        </td>
                        <td className="px-4 py-2 text-blue-500">{entry.timeTaken}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactionGame;
